import { useEffect, useState } from "react";
import DynamicForm from "@/components/DynamicForm";
import { lenderSubmissionSchema } from "@/schemas/lenderSubmissionSchema";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, Sparkles } from "lucide-react";

const lenderFields = [
  // Lender Information
  { name: "lenderName", label: "Lender/Institution Name", type: "text" as const, placeholder: "ABC Capital" },
  { name: "contactPerson", label: "Contact Person", type: "text" as const, placeholder: "Jane Smith" },
  { name: "email", label: "Email Address", type: "email" as const, placeholder: "jane@abccapital.com" },
  { name: "phone", label: "Phone Number", type: "tel" as const, placeholder: "+1 (555) 123-4567" },
  
  // Loan Product Details
  { 
    name: "productType", 
    label: "Product Type", 
    type: "select" as const,
    options: [
      { value: "term_loan", label: "Term Loan" },
      { value: "revolver", label: "Revolving Credit" },
      { value: "sba_loan", label: "SBA Loan" },
      { value: "bridge_loan", label: "Bridge Loan" },
      { value: "acquisition_financing", label: "Acquisition Financing" },
      { value: "equipment_financing", label: "Equipment Financing" },
    ]
  },
  { name: "loanAmount", label: "Maximum Loan Amount ($)", type: "text" as const, placeholder: "1000000" },
  { name: "interestRate", label: "Interest Rate (%)", type: "text" as const, placeholder: "7.5" },
  { name: "termMonths", label: "Loan Term (Months)", type: "text" as const, placeholder: "60" },
  
  // Deal Information
  { name: "dealId", label: "Deal ID", type: "text" as const, placeholder: "DEAL001" },
  { 
    name: "industryFocus", 
    label: "Industry Focus", 
    type: "select" as const,
    options: [
      { value: "manufacturing", label: "Manufacturing" },
      { value: "technology", label: "Technology" },
      { value: "healthcare", label: "Healthcare" },
      { value: "retail", label: "Retail" },
      { value: "services", label: "Services" },
      { value: "real_estate", label: "Real Estate" },
      { value: "all", label: "All Industries" },
    ]
  },
  
  // Terms & Conditions
  { 
    name: "conditions", 
    label: "Loan Conditions", 
    type: "textarea" as const,
    placeholder: "Describe specific requirements, covenants, or conditions (e.g., minimum DSCR, collateral requirements)",
    description: "Detail all terms and conditions for this loan product"
  },
  { 
    name: "collateralRequirements", 
    label: "Collateral Requirements (Optional)", 
    type: "textarea" as const,
    placeholder: "Specify collateral requirements if applicable"
  },
  { 
    name: "personalGuarantee", 
    label: "Personal Guarantee Required", 
    type: "checkbox" as const,
    description: "This loan product requires a personal guarantee"
  },
  
  // Financial Requirements
  { name: "minimumDSCR", label: "Minimum DSCR (Optional)", type: "text" as const, placeholder: "1.25" },
  { name: "minimumCreditScore", label: "Minimum Credit Score (Optional)", type: "text" as const, placeholder: "680" },
  { name: "maxLTV", label: "Maximum LTV (%) (Optional)", type: "text" as const, placeholder: "75" },
  
  // Additional Details
  { 
    name: "processingTime", 
    label: "Processing Time", 
    type: "select" as const,
    options: [
      { value: "1-2 weeks", label: "1-2 weeks" },
      { value: "2-4 weeks", label: "2-4 weeks" },
      { value: "1-2 months", label: "1-2 months" },
      { value: "2-3 months", label: "2-3 months" },
    ]
  },
  { 
    name: "fees", 
    label: "Fees Structure (Optional)", 
    type: "textarea" as const,
    placeholder: "Describe origination fees, closing costs, etc."
  },
  { 
    name: "specialFeatures", 
    label: "Special Features (Optional)", 
    type: "textarea" as const,
    placeholder: "Any unique aspects of this loan product"
  },
  { 
    name: "notes", 
    label: "Additional Notes (Optional)", 
    type: "textarea" as const,
    placeholder: "Any other relevant information"
  },
  
  // ──────────────────────────────────────────────────────────
  // NDA — Inline Non-Disclosure Agreement
  // Signing is mandatory and atomic with the offer submission.
  // The offer cannot be sent until ndaAcknowledge = true and a typed signature is provided.
  // ──────────────────────────────────────────────────────────
  {
    name: "ndaAcknowledge",
    label: "Non-Disclosure Agreement (NDA)",
    type: "checkbox" as const,
    description:
      "I acknowledge having read the Confidentiality Agreement attached to this opportunity (deal reference AQ-YYYY-D-NNNNN). I agree to keep all information shared by AcquizGroup, the Seller and any related party strictly confidential, to use it solely for the purpose of evaluating this financing opportunity, and to be bound by its terms. This electronic acknowledgement has the same legal effect as a handwritten signature."
  },
  {
    name: "ndaSignatureName",
    label: "Electronic Signature — Full Legal Name",
    type: "text" as const,
    placeholder: "e.g., Jane A. Smith",
    description: "Type your full legal name. This will be appended to the NDA and the offer PDF."
  },
  {
    name: "ndaSignatureTitle",
    label: "Title / Position",
    type: "text" as const,
    placeholder: "e.g., Senior Credit Officer",
  },

  // Confirmations
  { 
    name: "agreeToTerms", 
    label: "Terms and Conditions", 
    type: "checkbox" as const,
    description: "I confirm that all information provided is accurate and agree to the platform terms"
  },
];

type DealOption = { id: string; reference: string; businessName: string };

export default function LenderSubmissionPage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DealOption[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [selectedDealId, setSelectedDealId] = useState<string>("");

  useEffect(() => {
    (async () => {
      setLoadingDeals(true);
      try {
        const snap = await getDocs(query(collection(db, "deals"), orderBy("createdAt", "desc")));
        setDeals(snap.docs.map((d) => {
          const v = d.data() as any;
          return { id: d.id, reference: v.referenceNumber || "", businessName: v.businessName || "" };
        }));
      } catch (e) { console.error("Failed to load deals", e); }
      finally { setLoadingDeals(false); }
    })();
  }, []);

  const dealSelector = (
    <Card className="border-amber-500/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="w-4 h-4 text-amber-600" />
          Link this offer to a submitted deal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <select
          value={selectedDealId}
          onChange={(e) => setSelectedDealId(e.target.value)}
          disabled={loadingDeals}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">{loadingDeals ? "Loading deals…" : "— Select a deal —"}</option>
          {deals.map((d) => (
            <option key={d.id} value={d.id}>
              {(d.reference || `(${d.id.slice(0, 6)}…)`) + " — " + (d.businessName || "—")}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />
          Selecting a deal binds your offer to the right NDA & file.
        </p>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="py-6">
        <DynamicForm
          schema={lenderSubmissionSchema}
          fields={lenderFields}
          title="Lender Loan Submission"
          description="Submit your loan product details for review and matching with potential borrowers"
          collectionName="lender_submissions"
          draftKey={`lender_submission_draft_${selectedDealId || "unsaved"}`}
          showBackButton
          backPath="/dashboard/lender"
          extraHeader={dealSelector}
          onSuccess={() => {
            setTimeout(() => navigate("/lender/submit-offer"), 2000);
          }}
        />
      </div>
    </DashboardLayout>
  );
}
